import { Crepe, CrepeFeature } from '@milkdown/crepe'
import '@milkdown/crepe/theme/common/style.css'
import '@milkdown/crepe/theme/nord.css'
import { Check, Loader2, Save } from 'lucide-react'
import { useEffect, useRef, useSyncExternalStore } from 'react'
import {
  AUTO_SAVE_DEBOUNCE_MS,
  AUTO_SAVE_FEEDBACK_MS,
} from '@/hooks/use-auto-save'
import { trpc } from '@/lib/trpc'
import { useProblemOptional } from './problem-context'

type SaveState = 'idle' | 'typing' | 'saving' | 'saved'

function createSaveStateStore() {
  let state: SaveState = 'idle'
  const listeners = new Set<() => void>()
  return {
    getSnapshot: () => state,
    subscribe: (listener: () => void) => {
      listeners.add(listener)
      return () => {
        listeners.delete(listener)
      }
    },
    setState: (newState: SaveState) => {
      if (state === newState) return
      state = newState
      for (const l of listeners) l()
    },
  }
}

type SaveStateStore = ReturnType<typeof createSaveStateStore>

function SaveIndicator({ store }: { store: SaveStateStore }) {
  const saveState = useSyncExternalStore(store.subscribe, store.getSnapshot)

  return (
    <div className="text-muted-foreground flex items-center gap-1.5 text-xs">
      {saveState === 'typing' && (
        <>
          <Save className="h-3 w-3" />
          <span>Unsaved</span>
        </>
      )}
      {saveState === 'saving' && (
        <>
          <Loader2 className="h-3 w-3 animate-spin text-blue-600" />
          <span>Saving...</span>
        </>
      )}
      {saveState === 'saved' && (
        <>
          <Check className="h-3 w-3 text-green-600" />
          <span>Saved</span>
        </>
      )}
      {saveState === 'idle' && (
        // Invisible placeholder to reserve height and prevent layout shift
        <span className="invisible">Saved</span>
      )}
    </div>
  )
}

export function NotesPane() {
  const problem = useProblemOptional()
  const problemSlug = problem?.problemSlug ?? null
  const utils = trpc.useUtils()

  const storeRef = useRef(createSaveStateStore())
  const editorContainerRef = useRef<HTMLDivElement>(null)
  const crepeRef = useRef<Crepe | null>(null)
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const savedCheckTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const lastSavedContentRef = useRef('')
  const pendingMarkdownRef = useRef<string | null>(null)
  const isMountedRef = useRef(true)

  // Fetch existing note
  const noteQuery = trpc.notes.getByProblem.useQuery(
    { problemId: problemSlug! },
    { enabled: !!problemSlug },
  )

  // Save mutation
  const saveMutation = trpc.notes.save.useMutation()

  // Stable refs for use inside Crepe listener (avoids stale closures)
  const saveMutationRef = useRef(saveMutation)
  saveMutationRef.current = saveMutation
  const utilsRef = useRef(utils)
  utilsRef.current = utils

  // Latest rendered problem slug. Not the save target (that is the effect's
  // captured owningSlug) — this tracks what is *currently displayed*, so a save
  // that resolves after the user has navigated away can tell it is now stale.
  const activeSlugRef = useRef(problemSlug)
  activeSlugRef.current = problemSlug

  // Store query data in ref for use in effect without triggering re-creation
  const noteDataRef = useRef(noteQuery.data)
  noteDataRef.current = noteQuery.data

  // Create/destroy Milkdown editor when problemSlug changes and data is ready
  // biome-ignore lint/correctness/useExhaustiveDependencies: noteQuery.isLoading needed to re-trigger when query finishes (editor container isn't in DOM during loading)
  useEffect(() => {
    if (!editorContainerRef.current || !problemSlug) return

    // The problem this editor instance belongs to. Captured per effect run so a
    // pending save flushed during cleanup persists under the problem that was
    // current when the edit was made — not whatever problemSlug advanced to on
    // the next render (which would save problem A's note under problem B).
    const owningSlug = problemSlug

    const store = storeRef.current
    const initialContent = noteDataRef.current?.note?.content ?? ''
    lastSavedContentRef.current = initialContent
    store.setState('idle')

    // True while owningSlug is still the problem on screen. store and
    // lastSavedContentRef are shared across problems (the pane is reused, not
    // remounted), so a save that starts or resolves after the user navigates
    // away must not drive the visible status for the new problem.
    const isActiveProblem = () => activeSlugRef.current === owningSlug

    const handleSave = async (valueToSave: string) => {
      if (!isMountedRef.current) return

      // Only reflect 'saving' when this save belongs to the visible problem — a
      // background flush of the problem being left must stay silent, or it
      // leaves the newly opened problem's indicator stuck on 'Saving'.
      if (isActiveProblem()) store.setState('saving')

      try {
        const result = await saveMutationRef.current.mutateAsync({
          problemId: owningSlug,
          content: valueToSave,
        })

        if (!isMountedRef.current) return

        // Sync the query cache so remounts (e.g. layout changes) see the latest
        // content. Keyed by owningSlug, so this write is always correctly scoped
        // even if the user has since navigated to another problem.
        utilsRef.current.notes.getByProblem.setData(
          { problemId: owningSlug },
          {
            note: {
              id: result.note.id,
              content: valueToSave,
              updatedAt: result.note.updatedAt,
            },
          },
        )

        // If this save resolved after a switch, the shared store and
        // lastSavedContentRef now belong to a different problem — don't clobber
        // it with our status.
        if (!isActiveProblem()) return

        lastSavedContentRef.current = valueToSave
        store.setState('saved')

        savedCheckTimerRef.current = setTimeout(() => {
          if (isMountedRef.current) store.setState('idle')
        }, AUTO_SAVE_FEEDBACK_MS)
      } catch (error) {
        console.error('Note save failed:', error)
        if (isMountedRef.current && isActiveProblem()) {
          store.setState('typing')
        }
      }
    }

    const crepe = new Crepe({
      root: editorContainerRef.current,
      defaultValue: initialContent,
      features: {
        [CrepeFeature.Toolbar]: true,
        [CrepeFeature.ListItem]: true,
        [CrepeFeature.LinkTooltip]: true,
        [CrepeFeature.Cursor]: true,
        [CrepeFeature.CodeMirror]: false,
        [CrepeFeature.ImageBlock]: false,
        [CrepeFeature.BlockEdit]: false,
        [CrepeFeature.Table]: false,
        [CrepeFeature.Latex]: false,
      },
      featureConfigs: {
        [CrepeFeature.Placeholder]: {
          text: 'Write your markdown here (automatically rendered)...',
        },
      },
    })

    crepe.on((listener) => {
      listener.markdownUpdated((_ctx, markdown, prevMarkdown) => {
        if (markdown === prevMarkdown) return
        if (!isMountedRef.current) return

        if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current)
        if (savedCheckTimerRef.current) clearTimeout(savedCheckTimerRef.current)

        if (markdown === lastSavedContentRef.current) {
          pendingMarkdownRef.current = null
          store.setState('idle')
          return
        }

        pendingMarkdownRef.current = markdown
        store.setState('typing')

        debounceTimerRef.current = setTimeout(() => {
          pendingMarkdownRef.current = null
          handleSave(markdown)
        }, AUTO_SAVE_DEBOUNCE_MS.slow)
      })
    })

    crepe.create()
    crepeRef.current = crepe

    return () => {
      // Flush any pending unsaved content (fire-and-forget)
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current)
        debounceTimerRef.current = null
        const pending = pendingMarkdownRef.current
        if (pending !== null && pending !== lastSavedContentRef.current) {
          handleSave(pending)
        }
      }
      if (savedCheckTimerRef.current) clearTimeout(savedCheckTimerRef.current)

      crepe.destroy()
      crepeRef.current = null
      pendingMarkdownRef.current = null
    }
  }, [problemSlug, noteQuery.isLoading])

  // Cleanup on unmount
  useEffect(() => {
    isMountedRef.current = true
    return () => {
      isMountedRef.current = false
    }
  }, [])

  if (!problemSlug) {
    return (
      <div className="flex h-full items-center justify-center p-6">
        <p className="text-muted-foreground text-sm">No problem selected</p>
      </div>
    )
  }

  if (noteQuery.isLoading) {
    return (
      <div className="flex h-full items-center justify-center p-6">
        <Loader2 className="text-muted-foreground h-5 w-5 animate-spin" />
      </div>
    )
  }

  return (
    <div className="flex h-full flex-col">
      {/* Save status indicator — isolated re-renders via useSyncExternalStore */}
      <div className="flex items-center justify-end border-b border-gray-200 px-3 py-1.5 dark:border-gray-700">
        <SaveIndicator store={storeRef.current} />
      </div>

      {/* Milkdown editor fills remaining space */}
      <div
        ref={editorContainerRef}
        className="notes-editor min-h-0 flex-1 overflow-auto"
      />
    </div>
  )
}
